import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { TIER_META } from "@/components/Bits";
import { Users, Mail, MessageSquare, Trophy, TrendingUp, PoundSterling } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function Kpi({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="bg-white border border-gray-200 p-6 rounded-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="uppercase-tiny text-gray-500">{label}</div>
          <div className="font-heading text-3xl mt-3 tracking-tight">{value}</div>
          {hint && <div className="text-xs text-gray-500 mt-2">{hint}</div>}
        </div>
        <div className="w-8 h-8 border border-gray-200 rounded-sm flex items-center justify-center text-[#1B4332]">
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, t] = await Promise.all([
          api.get("/analytics/overview"),
          api.get("/analytics/timeline?days=30"),
        ]);
        setOverview(o.data);
        setTimeline(t.data.items);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmt = (n) => new Intl.NumberFormat("en-GB").format(n || 0);
  const gbp = (n) => "£" + fmt(n);

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;
  if (!overview) return null;

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="uppercase-tiny text-gray-500">Overview</div>
          <h1 className="font-heading text-4xl tracking-tight mt-1">Partnership Pipeline</h1>
          <p className="text-sm text-gray-600 mt-2">A snapshot of leads, outreach activity and projected revenue.</p>
        </div>
        <Link
          to="/leads"
          className="bg-[#1B4332] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#2D5A47] transition-colors duration-200"
          data-testid="dashboard-open-leads"
        >Manage Leads</Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Kpi icon={Users} label="Total Leads" value={fmt(overview.total_leads)} hint={`${fmt(overview.qualified)} qualified`} testid="kpi-total-leads" />
        <Kpi icon={Mail} label="Outreach Sent" value={fmt(overview.outreach_sent)} hint={`${fmt(overview.replies)} replied / negotiating`} testid="kpi-outreach-sent" />
        <Kpi icon={Trophy} label="Conversions" value={fmt(overview.conversions)} hint={`${fmt(overview.unsubscribed)} unsubscribed`} testid="kpi-conversions" />
        <Kpi icon={PoundSterling} label="Revenue Realised" value={gbp(overview.revenue_realised)} hint={`Projected: ${gbp(overview.revenue_projected)}`} testid="kpi-revenue-realised" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 p-6 rounded-sm" data-testid="timeline-card">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="uppercase-tiny text-gray-500">Activity — last 30 days</div>
              <div className="font-heading text-xl mt-1">Emails sent & conversions</div>
            </div>
            <TrendingUp className="h-4 w-4 text-[#1B4332]" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6B7280" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="sent" stroke="#1B4332" strokeWidth={2} dot={false} name="Sent" />
                <Line type="monotone" dataKey="conversions" stroke="#D4AF37" strokeWidth={2} dot={false} name="Conversions" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-6 rounded-sm" data-testid="revenue-by-tier-card">
          <div className="uppercase-tiny text-gray-500 mb-1">Revenue by tier</div>
          <div className="font-heading text-xl mb-4">Converted deals</div>
          <div className="space-y-3">
            {overview.revenue_by_tier.map((r) => (
              <div key={r.tier} className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0" data-testid={`revenue-row-${r.tier}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: TIER_META[r.tier]?.color }} />
                  <div>
                    <div className="text-sm">{r.name}</div>
                    <div className="text-xs text-gray-500">{r.converted_count} × £{r.price}</div>
                  </div>
                </div>
                <div className="font-mono text-sm">£{fmt(r.revenue)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
